import { useState, FormEvent, ChangeEvent, useEffect } from 'react'
import { X, Send, CheckCircle, AlertCircle } from 'lucide-react'
import emailjs from '@emailjs/browser'
import { EMAILJS_CONFIG } from '../config/emailjs'

interface InquiryModalProps {
  isOpen: boolean
  onClose: () => void
  serviceName: string
}

interface FormData {
  name: string
  email: string
  message: string
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

export default function InquiryModal({ isOpen, onClose, serviceName }: InquiryModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: '',
  })
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errors, setErrors] = useState<Partial<FormData>>({})

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', email: '', message: '' })
      setStatus('idle')
      setErrors({})
    }
  }, [isOpen])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setStatus('submitting')

    try {
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          service_type: serviceName,
        },
        EMAILJS_CONFIG.publicKey
      )
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-sm shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 flex items-center justify-between p-6 border-b bg-white"
          style={{ borderColor: 'var(--color-cream)' }}
        >
          <div>
            <p
              className="text-xs font-medium tracking-[0.15em] uppercase mb-1"
              style={{ color: 'var(--color-gold)' }}
            >
              Inquire About
            </p>
            <h3
              className="font-heading text-2xl"
              style={{ color: 'var(--color-dark)' }}
            >
              {serviceName}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close modal"
          >
            <X size={20} style={{ color: 'var(--color-dark)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {status === 'success' ? (
            <div className="text-center py-8">
              <CheckCircle
                size={48}
                className="mx-auto mb-4"
                style={{ color: 'var(--color-olive)' }}
              />
              <h4
                className="font-heading text-xl mb-2"
                style={{ color: 'var(--color-dark)' }}
              >
                Message Sent
              </h4>
              <p
                className="mb-6"
                style={{ color: 'var(--color-dark-light)' }}
              >
                Thank you for your interest! I'll be in touch soon.
              </p>
              <button
                onClick={onClose}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label
                  htmlFor="modal-name"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-dark)' }}
                >
                  Name
                </label>
                <input
                  type="text"
                  id="modal-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 transition-shadow"
                  style={{
                    borderColor: errors.name ? '#dc2626' : 'var(--color-sage)',
                    color: 'var(--color-dark)',
                  }}
                  placeholder="Your name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="modal-email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-dark)' }}
                >
                  Email
                </label>
                <input
                  type="email"
                  id="modal-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 transition-shadow"
                  style={{
                    borderColor: errors.email ? '#dc2626' : 'var(--color-sage)',
                    color: 'var(--color-dark)',
                  }}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="modal-message"
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--color-dark)' }}
                >
                  Message
                </label>
                <textarea
                  id="modal-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-white border focus:outline-none focus:ring-2 transition-shadow resize-none"
                  style={{
                    borderColor: errors.message ? '#dc2626' : 'var(--color-sage)',
                    color: 'var(--color-dark)',
                  }}
                  placeholder={`Tell me about your interest in ${serviceName.toLowerCase()}...`}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                )}
              </div>

              {/* Error message */}
              {status === 'error' && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle size={16} />
                  <p className="text-sm">Something went wrong. Please try again.</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? (
                  'Sending...'
                ) : (
                  <>
                    Send Inquiry
                    <Send size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
